import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Radio, Users, ShoppingCart, MessageCircle, Send, Play, Square, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "./Marketplace";

/**
 * MarketplaceLiveWatch — watch page for a single live session. Hosts see
 * their RTMP ingest details + Start/End controls. Viewers see the mock
 * video player, a pinned product carousel with Buy-now buttons, and a
 * simple text chat sidebar that polls every 3s.
 */
export default function MarketplaceLiveWatch() {
  const { sid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const chatSince = useRef(null);
  const chatBottom = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/marketplace/live/sessions/${sid}`);
      setSession(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // Bump viewer count on open
    api.post(`/marketplace/live/sessions/${sid}/viewer`).catch(() => {});
    api.get("/marketplace/config").then(r => setPaymentsEnabled(!!r.data?.payments_enabled)).catch(() => {});
  }, [sid]);

  // Chat poll
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const qs = chatSince.current ? `?since=${encodeURIComponent(chatSince.current)}` : "";
        const r = await api.get(`/marketplace/live/sessions/${sid}/chat${qs}`);
        if (!alive) return;
        const fresh = r.data?.items || [];
        if (fresh.length) {
          setChat(prev => [...prev, ...fresh]);
          chatSince.current = fresh[fresh.length - 1].created_at;
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [sid]);

  useEffect(() => {
    chatBottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.length]);

  if (!session) return <div className="max-w-5xl mx-auto px-6 py-16 text-gray-500">Loading…</div>;

  const isHost = user?.id === session.host_id;

  const sendMsg = async () => {
    if (!user) { nav("/login"); return; }
    if (!msg.trim()) return;
    try {
      await api.post(`/marketplace/live/sessions/${sid}/chat`, { text: msg.trim() });
      setMsg("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startStream = async () => { setBusy(true); try { const r = await api.post(`/marketplace/live/sessions/${sid}/start`); setSession(prev => ({ ...prev, ...r.data.session })); toast.success("You are live"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const endStream = async () => { if (!confirm("End this stream?")) return; setBusy(true); try { const r = await api.post(`/marketplace/live/sessions/${sid}/end`); setSession(prev => ({ ...prev, ...r.data.session })); toast.success("Stream ended"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };

  const buyListing = async (lid) => {
    if (!user) { nav("/login"); return; }
    try {
      const r = await api.post(`/marketplace/listings/${lid}/buy`, { origin_url: window.location.origin });
      window.open(r.data.url, "_blank", "noopener");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-6" data-testid="live-watch-page">
      <Link to="/marketplace/live" className="text-xs font-mono text-gray-500 hover:text-[#B31E1E]">← Back to Live</Link>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
        {/* Video + product carousel */}
        <div>
          <div className="aspect-video bg-black rounded-md overflow-hidden relative" data-testid="live-player">
            {session.status === "live" && session.is_mocked ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-white text-center p-6">
                <Radio size={40} className="mb-3 text-red-500 animate-pulse" />
                <p className="font-display text-2xl">LIVE (mock preview)</p>
                <p className="text-sm text-gray-300 mt-2 max-w-md">Real video ingest is wired to Mux Live — set <code className="font-mono text-xs">MUX_TOKEN_ID</code> + <code className="font-mono text-xs">MUX_TOKEN_SECRET</code> to see actual stream playback here.</p>
              </div>
            ) : session.status === "live" && session.playback_url ? (
              // TODO(mux): once real Mux is wired, render <video> with hls.js
              <video controls autoPlay className="w-full h-full" src={session.playback_url} data-testid="live-video" />
            ) : session.status === "scheduled" ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-white text-center">
                <Radio size={40} className="mb-3 text-amber-500" />
                <p className="font-display text-2xl">Upcoming</p>
                {session.scheduled_for && <p className="text-sm text-gray-300 mt-2">Starts {new Date(session.scheduled_for).toLocaleString()}</p>}
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white text-center">
                <p className="font-display text-2xl">Stream ended</p>
                {session.ended_at && <p className="text-sm text-gray-300 mt-2">{new Date(session.ended_at).toLocaleString()}</p>}
              </div>
            )}
            {session.status === "live" && (
              <>
                <Badge className="absolute top-3 left-3 rounded-sm bg-red-600 animate-pulse"><span className="w-2 h-2 rounded-full bg-white mr-1.5 inline-block" /> LIVE</Badge>
                <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"><Users size={11} /> {session.viewer_count}</div>
              </>
            )}
          </div>

          <div className="mt-4">
            <h1 className="font-display text-2xl">{session.title}</h1>
            <div className="text-sm text-gray-500 mt-1">Hosted by {session.host_type === "business" ? (session.host_business_name || session.host_name) : session.host_name}</div>
            {session.description && <p className="mt-3 text-sm text-gray-800 whitespace-pre-wrap">{session.description}</p>}
          </div>

          {isHost && (
            <HostControls session={session} onStart={startStream} onEnd={endStream} busy={busy} />
          )}

          {/* Product carousel */}
          {session.listings?.length > 0 && (
            <div className="mt-6">
              <div className="overline text-gray-500 mb-2">Shop the stream ({session.listings.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="live-listings">
                {session.listings.map(l => (
                  <Card key={l.id} className="rounded-md overflow-hidden">
                    <div className="aspect-square bg-gray-100">
                      {l.photos?.[0] ? <img src={l.photos[0]} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                    </div>
                    <div className="p-2">
                      <div className="text-xs truncate">{l.title}</div>
                      <div className="text-sm font-medium">{formatPrice(l.price_cents, l.currency)}</div>
                      {paymentsEnabled && l.status === "active" && l.price_cents > 0 && l.seller_id !== user?.id ? (
                        <Button size="sm" className="w-full mt-2 rounded-sm h-7 bg-[#B31E1E] hover:bg-[#8F1717] text-white" onClick={() => buyListing(l.id)} data-testid={`live-buy-${l.id}`}>
                          <ShoppingCart size={11} className="mr-1" /> Buy
                        </Button>
                      ) : (
                        <Link to={`/marketplace/listings/${l.id}`}><Button size="sm" variant="outline" className="w-full mt-2 rounded-sm h-7" data-testid={`live-view-${l.id}`}>View</Button></Link>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Shop products carousel — TikTok-Shop-style. Each card links to the
              product detail (Buy Now handled there via existing shop checkout). */}
          {session.products?.length > 0 && (
            <div className="mt-6" data-testid="live-shop-products">
              <div className="overline text-[#B31E1E] mb-2">Shop products ({session.products.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {session.products.map(p => (
                  <Card key={p.id} className="rounded-md overflow-hidden border-[#B31E1E]/20" data-testid={`live-product-${p.id}`}>
                    <div className="aspect-square bg-gray-100">
                      {p.photos?.[0] ? <img src={p.photos[0]} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><ShoppingCart size={22} /></div>}
                    </div>
                    <div className="p-2">
                      <div className="text-xs truncate">{p.title}</div>
                      <div className="text-sm font-medium">{formatPrice(p.price_cents, p.currency)}</div>
                      {p.business_id === user?.id ? (
                        <Link to={`/shop/${p.business_id}/product/${p.id}`}><Button size="sm" variant="outline" className="w-full mt-2 rounded-sm h-7" data-testid={`live-shop-view-${p.id}`}>View</Button></Link>
                      ) : (
                        <Link to={`/shop/${p.business_id}/product/${p.id}`}>
                          <Button size="sm" className="w-full mt-2 rounded-sm h-7 bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid={`live-shop-buy-${p.id}`}>
                            <ShoppingCart size={11} className="mr-1" /> Buy
                          </Button>
                        </Link>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Chat sidebar */}
        <Card className="rounded-md p-4 flex flex-col h-[600px]" data-testid="live-chat">
          <div className="flex items-center gap-2 pb-2 border-b border-border">
            <MessageCircle size={14} className="text-[#B31E1E]" />
            <div className="font-display text-sm">Live chat</div>
          </div>
          <div className="flex-1 overflow-y-auto py-2 space-y-1.5">
            {chat.length === 0 ? (
              <p className="text-xs text-gray-500 text-center pt-8">Say hi!</p>
            ) : chat.map(m => (
              <div key={m.id} className="text-sm" data-testid={`chat-msg-${m.id}`}>
                <span className="font-medium text-gray-900">{m.user_name || "Neighbor"}</span>
                <span className="text-[10px] text-gray-500 ml-2">{new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                <div className="text-gray-800 break-words">{m.text}</div>
              </div>
            ))}
            <div ref={chatBottom} />
          </div>
          <div className="pt-2 border-t border-border flex gap-2">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder={user ? "Send a message" : "Sign in to chat"} disabled={!user} data-testid="chat-input" className="rounded-sm text-sm" />
            <Button size="sm" onClick={sendMsg} disabled={!user || !msg.trim()} className="rounded-sm bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="chat-send"><Send size={12} /></Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function HostControls({ session, onStart, onEnd, busy }) {
  const copyKey = async () => {
    try { await navigator.clipboard.writeText(session.stream_key || ""); toast.success("Stream key copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <Card className="mt-4 rounded-md p-4 border-[#B31E1E]/30" data-testid="host-controls">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-600">
          <div className="font-semibold text-gray-900 text-sm mb-1">You are the host</div>
          Status: <Badge variant="outline" className="rounded-sm ml-1">{session.status}</Badge>
        </div>
        <div className="flex gap-2">
          {session.status !== "live" && session.status !== "ended" && (
            <Button size="sm" onClick={onStart} disabled={busy} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="host-start">
              <Play size={12} className="mr-1" /> Go live
            </Button>
          )}
          {session.status === "live" && (
            <Button size="sm" onClick={onEnd} disabled={busy} className="rounded-sm bg-gray-700 hover:bg-gray-800 text-white" data-testid="host-end">
              <Square size={12} className="mr-1" /> End stream
            </Button>
          )}
        </div>
      </div>
      {session.rtmp_url && session.stream_key && (
        <div className="mt-3 pt-3 border-t border-border text-xs">
          <div className="text-gray-600 mb-1">To broadcast from OBS/Streamlabs — RTMP server + key:</div>
          <div className="grid grid-cols-[80px_1fr] gap-2 items-center font-mono text-[11px]">
            <span className="text-gray-500">Server</span>
            <code className="bg-gray-100 px-2 py-1 rounded break-all">{session.rtmp_url}</code>
            <span className="text-gray-500">Key</span>
            <div className="flex items-center gap-2 min-w-0">
              <code className="bg-gray-100 px-2 py-1 rounded flex-1 truncate">{session.stream_key}</code>
              <button onClick={copyKey} className="text-gray-500 hover:text-[#B31E1E]" data-testid="copy-key"><Copy size={11} /></button>
            </div>
          </div>
          {session.is_mocked && <div className="mt-2 text-amber-600">⚠ Preview mock — no real ingest until Mux env vars are set.</div>}
        </div>
      )}
    </Card>
  );
}
